import React, { useState } from "react";
import AuthZButton from "../../../../components/AuthzButton";
import { AuthZTable } from "../../../../components/authZTable";
import ListKeyConditions from "../../../../components/condition/view/keyConditions";
import { MembershipConditionsController } from "./controller";
import "./index.scss";
import { Modal, Tooltip } from "antd";
import { UserTableColumnGenerator } from "../../../../components/InfoTab/UsersTable/userTableColumnGenerator";
import { ExclusionConfirmationModal } from "../../KeyCondition/SelectKeyCondition/ExclusionConfirmationModal";
import { MemberLegend } from "../../../../components/InfoTab/UsersTable";
import { useIsIndividualInclusionNamingEnabled } from "../../KeyCondition/common/hooks/IsIndividualInclusionNamingEnabled";

export const NoKeyConditionsMessage = "There are no key membership conditions";
export const NoIndividualInclusionsMessage = "There are no special conditions";
export const NoExcludedMembersMessage = "There are no excluded members";
export const AddKeyMembershipButtonText = "Add members";
export const InvalidKeyConditionText = "Invalid membership condition";

function MembershipConditions(
  {
    membershipCondition,
    individualInclusionMembers,
    haveMembershipConditionsEditAccess,
    userGroupUniqueName,
    userGroupTypeUniqueName,
  },
) {
  const {
    listOfKeyConditions,
    specialConditionsColumn,
    navigateToKeyConditionsPage,
    isMembershipConditionError,
    userGroupDetails,
    userGroupUsersDetails,
    excludedMembersDetails,
    excludedMembersColumns,
    updateExcludeRequest,
    getRemoveExcludedMemberMessage,
    onRemoveExcludedMemberConfirmation,
    onRemoveExcludedMemberCancel,
    shouldRenderRemoveExcludedMemberModal,
  } = MembershipConditionsController(
    membershipCondition,
    individualInclusionMembers,
    haveMembershipConditionsEditAccess,
    userGroupUniqueName,
    userGroupTypeUniqueName,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const handleOK = () => {
    if (selectedRows.length !== 0 && selectedRows.length !== excludedUserIds.length) {
      setIsConfirmationModalOpen(true);
    }
  };
  const [selectedRows, setSelectedRows] = useState([]);

  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled(); 

  const onSubmit = (reason) => {
    updateExcludeRequest(selectedRows, reason);
    setIsConfirmationModalOpen(false);
    setIsModalOpen(false);
  };
  const excludedUserIds = userGroupDetails?.excludedMembers?.map(x => x.userId) || [];

  let modifiedColumns = UserTableColumnGenerator(userGroupUsersDetails?.columns, individualInclusionMembers?.map(x => x.userId), true, true, excludedUserIds);

  function getDisabled(record) {
    return individualInclusionMembers?.map(x => x.userId)?.indexOf(record.key) > -1
      || excludedUserIds?.indexOf(record.key) > -1;
  }

  const defaultRowSelection = {
    defaultSelectedRowKeys: excludedUserIds,
    onChange: (selectedRowKeys, _selectedRows) => {
      setSelectedRows(userGroupUsersDetails?.dataSource.filter((datum) => selectedRowKeys.includes(datum.key)));
    },
    getCheckboxProps: (record) => ({
      disabled: getDisabled(record),
      name: record.key,
    }),
    preserveSelectedRowKeys: true,
    renderCell(checked, record, index, node) {
      if (individualInclusionMembers?.map(x => x.userId)?.indexOf(record.key) > -1) {
        return <Tooltip title="Individual inclusion members cannot be excluded" placement={"topRight"}>{node}</Tooltip>;
      }
      return node;
    },
  };

  const renderIndividualInclusionMembersTable = () => (
    <div className="conditions-container">
      <div className="conditions-heading">
        {isIndividualInclusionNamingEnabled ? "Individual Inclusions" : "Special conditions"} (
        {individualInclusionMembers?.length})
      </div>
      {individualInclusionMembers?.length === 0 ? (
        <div> {NoIndividualInclusionsMessage} </div>
      ) : (
        <div className="special-conditions-table">
          <AuthZTable
            name="individual-inclusion-members-list"
            rowKey="userId"
            dataSource={individualInclusionMembers}
            columns={specialConditionsColumn}
            pagination={false}
          />
        </div>
      )}
    </div>
  );

  const renderExcludedMembersTable = (haveMembershipConditionsEditAccess) => (
    <div className="conditions-container">
      <div className="conditions-heading">
        Excluded Members ({excludedMembersDetails?.length})
        {haveMembershipConditionsEditAccess ?
          <AuthZButton
            authZButtonType="primary"
            onClick={showModal}
            buttonStyle={{ marginLeft: "auto" }}
          >
            Exclude member(s)
          </AuthZButton> : null}
      </div>
      {excludedMembersDetails?.length === 0 ? (
        <div> {NoExcludedMembersMessage} </div>
      ) : (
        <div className="special-conditions-table">
          <AuthZTable
            name="individual-inclusion-members-list"
            rowKey="key"
            dataSource={excludedMembersDetails}
            columns={excludedMembersColumns}
            pagination={false}
          />
        </div>
      )}
    </div>
  );

  function renderRemoveExcludedMemberModal() {
    return (
      <Modal
        title="Remove Excluded Member"
        visible={shouldRenderRemoveExcludedMemberModal()}
        closable={false}
        onCancel={onRemoveExcludedMemberCancel}
        onOk={onRemoveExcludedMemberConfirmation}
        width={450}
        className="start-modal remove-exclusion-modal"
      >
        <div className="modal-content-body">
          <div className="modal-content-body-text">
            {getRemoveExcludedMemberMessage()}
          </div>
        </div>
      </Modal>
    );
  }

  const renderExcludeMembersModal = () => (
    <Modal
      title="Select member(s) to be excluded"
      visible={isModalOpen}
      closable={false}
      onCancel={handleCancel}
      onOk={handleOK}
      destroyOnClose={true}
      afterClose={() => setSelectedRows([])}
      width={"80vw"}
      className="exclude-members-modal"
    >
      <div style={{ float: "right", marginBottom: "10px" }}>
        <MemberLegend />
      </div>
      <AuthZTable
        name={"bt-exclude-members"}
        id={userGroupUniqueName}
        rowKey={"key"}
        columns={modifiedColumns}
        dataSource={userGroupUsersDetails?.dataSource}
        pagination={false}
        rowSelection={{ type: "checkbox", ...defaultRowSelection }}
        scroll={{ y: "calc(50vh)" }}
      />
    </Modal>
  );

  return (
    <>
      {isMembershipConditionError === true ? (
        <div className="error-text error-padding">{InvalidKeyConditionText}</div>
      ) : (
        <div className="conditions-container">
          <div className="key-conditions-container-header">
            <div className="conditions-heading">
              Total key conditions ({listOfKeyConditions.length})
            </div>
            {haveMembershipConditionsEditAccess === true ? (
              <AuthZButton
                authZButtonType="primary"
                onClick={navigateToKeyConditionsPage}
              >
                Add members
              </AuthZButton>
            ) : null}
          </div>
          {listOfKeyConditions.length === 0 ? (
            <div>{NoKeyConditionsMessage}</div>
          ) : (
            <ListKeyConditions
              listOfKeyConditions={listOfKeyConditions}
              haveMembershipConditionsEditAccess={haveMembershipConditionsEditAccess}
              userGroupUniqueName={userGroupUniqueName}
              userGroupTypeUniqueName={userGroupTypeUniqueName}
            />
          )}
        </div>
      )}
      {renderIndividualInclusionMembersTable()}
      {renderExcludedMembersTable(haveMembershipConditionsEditAccess)}
      {renderExcludeMembersModal()}
      {renderRemoveExcludedMemberModal()}
      <ExclusionConfirmationModal
        users={selectedRows.filter(x => excludedUserIds.indexOf(x.key) === -1)}
        isVisible={isConfirmationModalOpen}
        setModalVisibility={setIsConfirmationModalOpen}
        onConfirm={onSubmit}
      />
    </>
  );
}

export default MembershipConditions;
